import financialAnalysisService from '../../../core/services/Fundamental/FinancialAnalysisService';
import snapShotService from '../../../core/services/Fundamental/SnapShotService';
import priceDataService from '../../../core/services/Prices/PriceData/PriceDataService';
import {
  checkIsBank,
  getQueryParams,
  getSyncDefaultSearch,
} from '../../helpers/Common';
import reducerRegistry from '../../helpers/ReducerRegistry';
import { INDUSTRY, QUARTERLY, YEARLY } from './constants';
import { formatFinancialRatioData } from './helper';
import CurrentState from '../../helpers/CurrentState';
import ConstCommon from '../../common/Const';

export const REDUCER_NAME = 'FINANCIAL_RATIO';
export const FETCH_DATA_SUCCESS = `${REDUCER_NAME}FETCH_DATA_SUCCESS`;
export const ADD_COMPARE = `${REDUCER_NAME}ADD_COMPARE`;
export const REMOVE_COMPARE = `${REDUCER_NAME}REMOVE_COMPARE`;
export const CHANGE_TIME_FILTER = `${REDUCER_NAME}CHANGE_TIME_FILTER`;
export const CHANGE_FILTER_INDUSTRY = `${REDUCER_NAME}CHANGE_FILTER_INDUSTRY`;
export const SET_LOADING = `${REDUCER_NAME}SET_LOADING`;
export const INIT_COMPONENT = `${REDUCER_NAME}INIT_COMPONENT`;
export const UNMOUNT_COMPONENT = `${REDUCER_NAME}UNMOUNT_COMPONENT`;
export const RESET_COMPARE = `${REDUCER_NAME}RESET_COMPARE`;
export const RESET_DATA = `${REDUCER_NAME}RESET_DATA`;
export const GET_COMPANY_SCORE = `${REDUCER_NAME}GET_COMPANY_SCORE`;
export const GET_TOP_CONTENT = `${REDUCER_NAME}GET_TOP_CONTENT`;
export const CHANGE_SEARCH_PARAMS = `${REDUCER_NAME}CHANGE_SEARCH_PARAMS`;
export const SET_HIDE_INDICATOR = `${REDUCER_NAME}SET_HIDE_INDICATOR`;
export const SET_REALTIME_DATA = `${REDUCER_NAME}SET_REALTIME_DATA`;
export const FETCH_ONE_DATA_SUCCESS = `${REDUCER_NAME}FETCH_ONE_DATA_SUCCESS`;

export const setRealtimeData = (componentId, data) => ({
  type: SET_REALTIME_DATA,
  payload: {
    topContentId: componentId,
    topContent: { priceInfo: data },
  },
});

export const setHideIndicator = (componentId, indicators) => ({
  type: SET_HIDE_INDICATOR,
  payload: { componentId, indicators },
});

export const changeSearchParams = (id, searchParams) => ({
  payload: {
    searchParams,
    componentId: id,
  },
  type: CHANGE_SEARCH_PARAMS,
});

export const resetData = componentId => ({
  type: RESET_DATA,
  payload: { componentId },
});

export const resetCompares = componentId => ({
  type: RESET_COMPARE,
  payload: { componentId },
});

export const unmountComponent = componentId => ({
  type: UNMOUNT_COMPONENT,
  payload: componentId,
});

export const setLoading = (componentId, isLoading = true) => ({
  type: SET_LOADING,
  payload: {
    componentId,
    isLoading,
  },
});

export const changeFilterIndustry = payload => ({
  payload,
  type: CHANGE_FILTER_INDUSTRY,
});

export const addCompare = payload => {
  return {
    payload,
    type: ADD_COMPARE,
  };
};

export const removeCompare = payload => {
  return {
    payload,
    type: REMOVE_COMPARE,
  };
};

export const fetchCompanyScore = (id, code) => {
  return dispatch => {
    snapShotService.getCompanyScore({ OrganCode: code }).then(response => {
      const { items = [] } = response;
      dispatch({
        type: GET_COMPANY_SCORE,
        payload: {
          companyScoreId: id,
          companyScore: items && items[0] ? items[0] : {},
        },
      });
    });
  };
};

export const fetchInfoTicker = (id, code) => {
  return dispatch => {
    priceDataService.getLatestPrice({ Code: code }).then(response => {
      const { items = [] } = response;
      dispatch({
        type: GET_TOP_CONTENT,
        payload: {
          topContentId: id,
          topContent: items && items.length ? items[0] : {},
        },
      });
    });
  };
};

export const fetchData = ({ componentId, timelines, mapping }) => (
  dispatch,
  getState,
) => {
  dispatch(setLoading(componentId));
  const { compares, searchParams, isFilterIndustry } = getState()[REDUCER_NAME][
    componentId
  ];
  const organCodes = [searchParams.code];
  compares.forEach(compare => {
    organCodes.push(compare.code);
  });

  const promises = organCodes.map(code => {
    const params = {
      Type: 'Company',
      OrganCode: code,
      Timeline: timelines,
    };

    return financialAnalysisService
      .getFinancialRatio(params)
      .then(res => res.items);
  });

  if (isFilterIndustry) {
    organCodes.push(INDUSTRY);
    promises.push(
      financialAnalysisService
        .getFinancialRatio({
          Type: 'Icb',
          OrganCode: searchParams.code,
          Timeline: timelines,
        })
        .then(res => res.items),
    );
  }

  Promise.all(promises).then(data => {
    const finalData = {};
    organCodes.forEach((organCode, index) => {
      finalData[organCode] = formatFinancialRatioData(data[index], mapping);
    });
    dispatch(setLoading(componentId, false));
    if (timelines.length > 1) {
      dispatch(
        fetchDataSuccess({
          componentId,
          data: finalData,
        }),
      );
    } else {
      dispatch(
        fetchOneDataSuccess({
          componentId,
          data: finalData,
        }),
      );
    }
  });
};

export const fetchOneDataSuccess = payload => ({
  type: FETCH_ONE_DATA_SUCCESS,
  payload,
});

export const fetchDataSuccess = payload => ({
  type: FETCH_DATA_SUCCESS,
  payload,
});

export const changeTimeFilter = payload => ({
  type: CHANGE_TIME_FILTER,
  payload,
});

const initialData = {
  compares: [],
  data: {},
  timeFilter: QUARTERLY,
  isFilterIndustry: false,
  isLoading: false,
  topContent: {},
  topContentRealTime: {},
  companyScore: {},
  searchParams: {},
  hideIndicators: {},
};

const initialState = {};

const getDefaultSearchParams = (initCode, action) => {
  let defaultSearchParams = {
    displayCode: 'SSI',
    code: 'SSI',
    isBanking: false,
  };
  if (initCode) {
    defaultSearchParams = {
      displayCode: initCode.ticker,
      code: initCode.organCode,
      isBanking: checkIsBank(initCode.comTypeCode),
    };
  }

  return (
    CurrentState.get(
      ConstCommon.listComponent.FinancialRatio,
      'searchParams',
      action.payload.id,
    ) ||
    getSyncDefaultSearch(ConstCommon.listComponent.FinancialRatio, true) ||
    defaultSearchParams
  );
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case INIT_COMPONENT:
      const initCode = action.payload.dragCode;

      const compares =
        CurrentState.get(
          ConstCommon.listComponent.FinancialRatio,
          'compares',
          action.payload.id,
        ) || [];
      const isFilterIndustry =
        CurrentState.get(
          ConstCommon.listComponent.FinancialRatio,
          'isFilterIndustry',
          action.payload.id,
        ) || false;
      const mapTimeFilter = {
        quarterly: QUARTERLY,
        yearly: YEARLY,
      };

      return {
        ...state,
        [action.payload.id]: {
          ...initialData,
          compares,
          isFilterIndustry,
          searchParams: getDefaultSearchParams(initCode, action),
          hideIndicators:
            CurrentState.get(
              ConstCommon.listComponent.FinancialRatio,
              'hideIndicators',
              action.payload.id,
            ) || {},
          timeFilter: mapTimeFilter[getQueryParams('period')] || QUARTERLY,
        },
      };

    case UNMOUNT_COMPONENT:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          data: {},
        },
      };
    case FETCH_DATA_SUCCESS:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          data: {
            ...state[action.payload.componentId].data,
            ...action.payload.data,
          },
        },
      };
    case FETCH_ONE_DATA_SUCCESS:
      const stateData = state[action.payload.componentId].data;
      const newData = {};
      Object.keys(stateData).forEach(key => {
        newData[key] = {
          ...action.payload.data[key],
          ...stateData[key],
        };
      });

      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          data: newData,
        },
      };
    case ADD_COMPARE:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          compares: state[action.payload.componentId].compares.concat(
            action.payload.organization,
          ),
        },
      };

    case REMOVE_COMPARE:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          compares: state[action.payload.componentId].compares.filter(
            org => org.organCode !== action.payload.organCode,
          ),
        },
      };

    case CHANGE_TIME_FILTER:
      CurrentState.add(
        ConstCommon.listComponent.FinancialRatio,
        'timeFilter',
        action.payload.timeFilter,
        action.payload.componentId,
      );

      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          timeFilter: action.payload.timeFilter,
        },
      };
    case CHANGE_FILTER_INDUSTRY:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          isFilterIndustry: action.payload.checked,
        },
      };
    case SET_LOADING:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          isLoading: action.payload.isLoading,
        },
      };
    case RESET_COMPARE:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          compares: [],
        },
      };
    case RESET_DATA:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          data: {},
        },
      };
    case GET_COMPANY_SCORE:
      return {
        ...state,
        [action.payload.companyScoreId]: {
          ...state[action.payload.companyScoreId],
          companyScore: action.payload.companyScore,
        },
      };
    case CHANGE_SEARCH_PARAMS:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          searchParams: action.payload.searchParams,
          compares: [],
          isFilterIndustry: false,
        },
      };
    case SET_HIDE_INDICATOR:
      return {
        ...state,
        [action.payload.componentId]: {
          ...state[action.payload.componentId],
          hideIndicators: action.payload.indicators,
        },
      };
    case SET_REALTIME_DATA:
    case GET_TOP_CONTENT:
      const {
        topContent: { priceInfo },
        topContentId,
      } = action.payload;
      const MatchPrice = priceInfo ? priceInfo.matchPrice : null;
      const PriceChange = priceInfo ? priceInfo.priceChange : null;
      const PercentPriceChange = priceInfo
        ? priceInfo.percentPriceChange
        : null;
      const ReferencePrice = priceInfo ? priceInfo.referencePrice : null;

      const CeilingPrice = priceInfo ? priceInfo.ceilingPrice : null;
      const FloorPrice = priceInfo ? priceInfo.floorPrice : null;

      const topContent = {
        MatchPrice,
        PriceChange,
        PercentPriceChange,
        ReferencePrice,
        CeilingPrice,
        FloorPrice,
      };

      return {
        ...state,
        [topContentId]: {
          ...state[topContentId],
          topContent,
        },
      };
    default:
      return state;
  }
};

reducerRegistry.register(REDUCER_NAME, reducer);
